import { useId, useState, type FormEvent } from "react";
import { Plus, X } from "lucide-react";
import type { CreditNoteInput } from "@/api/invoices";
import { RequestError } from "@/components/RequestError";
import { Button } from "@/components/ui/button";
import { TextAreaField, TextField } from "@/components/ui/field";
import { FormActions } from "@/components/ui/form-actions";
import { formatMoney } from "@/lib/format";
import { centsToAmount } from "@/lib/money";
import { useSubmitState } from "@/lib/use-form";

type CreditNoteFormProps = {
  currency: string;
  /** What is left to credit on the invoice: its total less the credit notes issued. */
  maxAmount: string;
  onSubmit: (input: CreditNoteInput) => Promise<unknown>;
  onCancel: () => void;
  onReload?: () => void;
};

type Row = { key: number; description: string; amount: string };

function cents(value: string): number {
  const amount = Number(value);
  return Number.isFinite(amount) ? Math.round(amount * 100) : 0;
}

/** A credit note against the invoice: why, and what is credited, line by line. It is saved
 *  as a draft to preview and issue. */
export function CreditNoteForm({
  currency,
  maxAmount,
  onSubmit,
  onCancel,
  onReload,
}: CreditNoteFormProps) {
  const id = useId();
  const [invoiceDate, setInvoiceDate] = useState("");
  const [reason, setReason] = useState("");
  const [rows, setRows] = useState<Row[]>([{ key: 1, description: "", amount: "" }]);
  const { error, errors, saving, run } = useSubmitState();
  const total = rows.reduce((sum, row) => sum + cents(row.amount), 0);

  function change(key: number, field: "description" | "amount", value: string): void {
    setRows((current) =>
      current.map((row) => (row.key === key ? { ...row, [field]: value } : row)),
    );
  }

  function handleSubmit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    void run(() =>
      onSubmit({
        invoice_date: invoiceDate || null,
        reason: reason.trim(),
        lines: rows
          .filter((row) => row.description.trim() || row.amount.trim())
          .map((row) => ({
            description: row.description.trim(),
            detail: "",
            amount: row.amount.trim(),
          })),
      }),
    );
  }

  return (
    <form onSubmit={handleSubmit} noValidate className="flex flex-col gap-4">
      <RequestError error={error} onReload={onReload} />
      <div className="grid gap-4 md:grid-cols-3">
        <TextField
          id={`${id}-date`}
          label="Date"
          type="date"
          value={invoiceDate}
          onChange={setInvoiceDate}
          error={errors.invoice_date}
          hint="Leave empty for today"
        />
        <TextAreaField
          id={`${id}-reason`}
          label="Reason"
          value={reason}
          onChange={setReason}
          error={errors.reason}
          rows={2}
          className="md:col-span-2"
          hint="Printed on the credit note"
        />
      </div>
      <ul className="flex flex-col gap-3">
        {rows.map((row, index) => (
          <li key={row.key} className="grid gap-3 sm:grid-cols-[1fr_10rem_auto] sm:items-end">
            <TextField
              id={`${id}-line-${row.key}`}
              label={`Line ${index + 1}`}
              value={row.description}
              onChange={(value) => change(row.key, "description", value)}
            />
            <TextField
              id={`${id}-amount-${row.key}`}
              label={`Amount ${index + 1} (${currency})`}
              inputMode="decimal"
              value={row.amount}
              onChange={(value) => change(row.key, "amount", value)}
            />
            {rows.length > 1 && (
              <Button
                type="button"
                size="sm"
                variant="ghost"
                aria-label={`Remove line ${index + 1}`}
                onClick={() => setRows((current) => current.filter((r) => r.key !== row.key))}
              >
                <X className="h-4 w-4" aria-hidden />
              </Button>
            )}
          </li>
        ))}
      </ul>
      {errors.lines && <p className="text-sm text-destructive">{errors.lines}</p>}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <Button
          type="button"
          size="sm"
          variant="outline"
          onClick={() =>
            setRows((current) => [
              ...current,
              { key: Math.max(...current.map((r) => r.key)) + 1, description: "", amount: "" },
            ])
          }
        >
          <Plus className="h-4 w-4" aria-hidden />
          Add a line
        </Button>
        <p className="text-sm">
          Total credit {formatMoney(centsToAmount(total), currency)} of{" "}
          {formatMoney(maxAmount, currency)} that can be credited
        </p>
      </div>
      <FormActions saving={saving} submitLabel="Draft credit note" onCancel={onCancel} />
    </form>
  );
}
